import { z } from "zod";

/**
 * Messenger connect hand-off: Meta redirects to one fixed console URL, outside `/w/:ws`, so the
 * workspace + agent that started the flow are parked in sessionStorage (same tab, no secrets —
 * the OAuth `state` itself is held server-side in Redis).
 */
export const PENDING_META_CONNECT_KEY = "sagot:meta-connect";
const MAX_AGE_MS = 15 * 60_000;

const pendingSchema = z.object({
  wsSlug: z.string().min(1).max(100),
  wsId: z.string().uuid(),
  agentId: z.string().uuid(),
  startedAt: z.number().int(),
});
export type PendingMetaConnect = z.infer<typeof pendingSchema>;

export function savePendingConnect(value: Omit<PendingMetaConnect, "startedAt">, now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(PENDING_META_CONNECT_KEY, JSON.stringify({ ...value, startedAt: now }));
  } catch {
    // Storage blocked (private mode): the callback shows the "start again" state.
  }
}

export function readPendingConnect(now: number = Date.now()): PendingMetaConnect | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_META_CONNECT_KEY);
    if (!raw) return null;
    const parsed = pendingSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || now - parsed.data.startedAt > MAX_AGE_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

export function clearPendingConnect(): void {
  try {
    window.sessionStorage.removeItem(PENDING_META_CONNECT_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
